import * as React from 'react';
import { useParams } from 'react-router-dom';
import { UserToken, PublicAmorgUser, Activity, Survey, SurveyReport } from '../../../amorg-common';
import { getActivity } from '../../../api/activityClientApi';
import { getAuthorizedActiveUserOrRedirect } from '../../../api/serverApiHelpers';
import { getSurveyByActivityId, getSurveyReport } from '../../../api/surveyClientApi';
import { ProtectedPageWrapper } from '../../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { Tabber } from '../../../designSystem/Tabber';
import AnswersPanel from './_components/tabs/answer/AnswersPanel';
import { ReportPanel } from './_components/tabs/report/ReportPanel';
import { SurveyPanel } from './_components/tabs/survey/SurveyPanel';
import { getUsers } from '../../../api/userClientApi';
import { WorkplaceAssessmentComponentProps } from './_components/tabs/_components/WorkplaceAssessmentComponentProps';
import { ActionPlansPanel } from './_components/tabs/actionplan/ActionPlansPanel';
import { OverviewPanel } from './_components/tabs/overview/OverviewPanel';

export default function WorkplaceAssessmentPage(): JSX.Element {
    
    const params = useParams<{ activityId: string }>();
    return (
        <ProtectedPageWrapper requiredPermissions={['ViewActivity']} selectedMenuItemKey="workplaceassessment">
            {params.activityId 
                ? <WorkPlaceAssessmentPageView activityId={params.activityId} />
                : <span>ugyldig url</span>
        }
        </ProtectedPageWrapper>
    );
}


type State ={
    loading: boolean,
    activeUser: UserToken | null,
    users: PublicAmorgUser[],
    activity:Activity | null,
    survey: Survey | null,
    surveyReport: SurveyReport | null,
}

type WorkPlaceAssessmentPageViewProps =
{
    activityId:string
}

function WorkPlaceAssessmentPageView(props:WorkPlaceAssessmentPageViewProps): JSX.Element {
    const [state, setState] = React.useState<State>(
        {
            loading: true,
            activeUser: null,
            activity: null,
            users: [],
            survey: null,
            surveyReport: null
        }
    )
    const [selectedTab, setSelectedTab] = React.useState('overview');

    React.useEffect(function(){
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        let tab = urlParams.get('tab');
        if(!['overview','survey','answers','actionplans','report'].find(t => t === tab))
            tab = 'overview';
        setSelectedTab(tab as string);
    }, [window.location.search]);

    React.useEffect(function () {
        updateState();
    }, [props.activityId]);

    async function updateState() {
        setState({...state, loading:true});
        const activeUser = await getAuthorizedActiveUserOrRedirect(['ViewActivity']);
        const activity = await getActivity(props.activityId);
        if(!activity)
            return;

        const users = await getUsers();
        const survey = await getSurveyByActivityId(activity.id);
        if (survey !== null) {
            const surveyReport = await getSurveyReport(survey.id);
            setState({
                loading:false,
                activeUser,
                activity,
                users,
                survey,
                surveyReport
            });
        }
        else {
            setState({
                loading:false,
                activeUser,
                activity,
                users,
                survey: null,
                surveyReport: null
            });
        }
    }



    const componentProps:WorkplaceAssessmentComponentProps | null = 
        state.activity && state.activeUser
        ? {
                activeUser:state.activeUser,
                users:state.users,
                activity:state.activity,
                surveyLoading:state.loading,
                survey:state.survey,
                surveyReport:state.surveyReport,
                onActivityUpdated:() => {
                    updateState();
                },
                onSurveyUpdated:() => updateState()
            }
        : null;

    return (
        <>
            <LoadingPageOverlay loading={state.loading} />
            {state.activity &&
                <SingleColumnLayout
                    noDivider
                    wide
                    contentContainer={
                        <>
                            <Tabber
                                items={[
                                    { tabName:'overview', text:'Oversigt'},
                                    { tabName:'survey', text:'Spørgeskema', disabled:state.survey===null},
                                    { tabName:'answers', text:'Besvarelser', disabled:state.survey===null},
                                    { tabName:'report', text:'Rapport', disabled:state.survey===null||state.survey.finishedAnswerCount===0},
                                    { tabName:'actionplans', text:'Handleplaner'},
                                ]}
                                selectedTabName={selectedTab}
                                onClick={(tabName) => {
                                    window.history.replaceState(null, '', `/workplaceassessments/${props.activityId}?tab=${tabName}`);
                                    setSelectedTab(tabName);
                                }}
                            />

                            {componentProps &&
                                <>
                                    {selectedTab === 'overview' &&
                                        <OverviewPanel
                                            {...componentProps} />
                                    }

                                    {selectedTab === 'survey' && state.survey &&
                                        <SurveyPanel {...componentProps} />
                                    }

                                    {selectedTab === 'answers' && state.survey &&
                                        <AnswersPanel {...componentProps} />
                                    }

                                    {selectedTab === 'report' && state.survey &&
                                        <ReportPanel  {...componentProps} />
                                    }

                                    {selectedTab === 'actionplans' &&
                                        <ActionPlansPanel  {...componentProps} />
                                    }
                                </>
                            }
                        </>
                    }
                />
            }
        </>
    );
}